import React from 'react';
const { useState, useMemo } = React;
import QuoteEditor from './QuoteEditor.jsx';
import { headOf, totalsText, recordTotalText } from './QuoteList.jsx';
import { saveQuoteRecord, approveQuoteRecord, publishQuoteRecord, acceptQuoteRecord, quoteErrorMessage } from '../../quoteActions.js';
import { recipientOf, classifyRecipient } from '../../personQuotes.js';
import { isAccepted, wasAccepted } from '../../quoteRules.js';
import { fileKindOf, snapshotOf, sendsOf, hasUnpublishedChanges, acceptanceOf, sendProblem, acceptProblem, ACCEPTED_VIA, declineOf } from '../../quoteFile.js';
import { paymentNotesOf } from '../../quoteChains.js';
import FileSummary, { StagePill } from './FileSummary.jsx';
import ShareForResponse from './ShareForResponse.jsx';

// The single file of a quote (docs/servicios/cotizaciones.md › «Ficha única»), the same from «Cotizaciones y
// cobros», the company, Contactos/Personas, the Pipeline, Hoy and Ingresos. One record, edited until the client
// accepts it: saving, reviewing or sending never creates another. Sending again updates the same document and
// link and keeps the date and total of each send. The acceptance (registered here, or confirmed by the client
// from her space) freezes the accepted document. Earlier versions created before stay as read-only history.
// The server decides what may change and who may do it (src/quoteRules.js, publish-proposal, quote-accept).

const muted = { fontSize: 12, color: 'var(--globant-muted)' };
const section = { fontSize: 11, fontWeight: 800, color: 'var(--globant-muted)', letterSpacing: 1, textTransform: 'uppercase', margin: '4px 0 6px' };
const day = (v) => (v ? new Date(`${String(v).slice(0, 10)}T12:00:00Z`).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const when = (v) => { if (!v) return ''; const d = new Date(v); return Number.isNaN(d.getTime()) ? day(v) : d.toLocaleString('es', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
const todayInput = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const docLink = (landing) => (landing?.fields?.['Proposal Slug'] ? `/proposal.html?slug=${encodeURIComponent(landing.fields['Proposal Slug'])}` : '');
const SEND_BLOCK = { quote_no_lines: 'Agregá al menos una línea y guardala para publicar.', quote_lost: 'Está marcada como perdida: no se publica.', quote_replaced: 'Es una versión anterior.' };
const ACCEPT_BLOCK = {
  unpublished_changes: 'Hay cambios que el cliente todavía no recibió: publicalos (o deshacelos) antes de registrar la aceptación. Se acepta lo que el cliente tiene.',
  quote_lost: 'Está marcada como perdida.', quote_no_lines: 'Todavía no tiene líneas.', quote_replaced: 'Es una versión anterior.', quote_was_accepted: 'Ya fue aceptada antes y la aceptación se corrigió.',
};

/**
 * chain (src/quoteChains.js); title, subtitle; landingOf(record) → its document (Landings) or null;
 * intakeOf(record) → the request whose answers help the internal analysis; accountOf(record);
 * contactName (who usually accepts); onPatched(id, fields); onLanding(landing) after a send;
 * billing(record) → the payments block of a contratación (rendered only once accepted); onBack.
 */
export default function QuoteFile({ chain, title, subtitle = '', landingOf = () => null, intakeOf = () => null, accountOf = () => null, contactName = '', api, onPatched, onLanding = () => {}, billing = null, onBack = null, summary = null, top = null, share = null }) {
  const rec = headOf(chain);
  const f = rec.fields || {};
  const kind = fileKindOf(f);
  const recipient = recipientOf(f);
  const landing = landingOf(rec);
  const snapshot = snapshotOf(landing);
  const sends = sendsOf(snapshot, landing);
  const pending = !wasAccepted(f) && hasUnpublishedChanges(f, snapshot);
  const acceptance = acceptanceOf(snapshot);
  const request = intakeOf(rec);
  const earlier = useMemo(() => chain.versions.filter(v => v.id !== rec.id).reverse(), [chain, rec.id]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [accepting, setAccepting] = useState(null); // { day, time, by, confirm }
  const [viewEarlier, setViewEarlier] = useState('');

  const act = async (fn, done, failText) => {
    setSaving(true); setMsg('');
    try { const fields = await fn(); if (fields) onPatched(rec.id, fields); setMsg(done); }
    catch (e) { setMsg(quoteErrorMessage(e, failText)); }
    setSaving(false);
  };
  // After `link_failed`, the document already created: the retry reuses it on this same record (never another)
  const [orphanLanding, setOrphanLanding] = useState('');
  const send = async () => {
    setSending(true); setMsg('');
    try {
      const r = await publishQuoteRecord(rec.id, { landingId: orphanLanding });
      setOrphanLanding('');
      onPatched(rec.id, { 'Consulting Landing': [r.landingId] });
      onLanding({ id: r.landingId, fields: { ...(landing?.fields || {}), 'Proposal Slug': r.proposalSlug, 'Published At': r.publishedAt, 'Proposal Snapshot JSON': JSON.stringify(r.snapshot), Status: 'Published' } });
      setMsg(`${r.updated ? 'Cambios publicados en el mismo documento y enlace.' : 'Publicada.'} ${recipient.value === 'person' ? 'La persona la ve en su espacio.' : 'Compartí el enlace del documento con el cliente.'}`);
    } catch (e) {
      if (e.code === 'link_failed' && e.landingId) setOrphanLanding(e.landingId);
      if (e.code === 'landing_mismatch') setOrphanLanding('');
      setMsg(quoteErrorMessage(e, 'No se pudo publicar'));
    }
    setSending(false);
  };
  const accept = async () => {
    setSaving(true); setMsg('');
    try {
      const r = await acceptQuoteRecord(rec.id, { day: accepting.day, time: accepting.time, by: accepting.by });
      if (r.existing) setMsg('Ya estaba aceptada: no se registró de nuevo.');
      else {
        onPatched(rec.id, r.fields);
        if (landing && snapshot) onLanding({ ...landing, fields: { ...landing.fields, 'Proposal Snapshot JSON': JSON.stringify({ ...snapshot, acceptance: { at: r.fields['Won At'], by: r.fields['Accepted By'], via: 'registered' } }) } });
        setMsg(`Aceptación registrada: ahora es una contratación y su documento queda como se aceptó.${r.viaPending ? ' (No se pudo guardar «Accepted Via» porque el campo no está en Airtable: el «cómo» quedó guardado con el documento.)' : ''}`);
      }
      setAccepting(null);
    } catch (e) { setMsg(e.message); }
    setSaving(false);
  };

  const canSend = !sendProblem(f);
  const acceptBlock = acceptProblem(f, snapshot);
  // How it was accepted: the field, or what the document kept; empty (acceptances before the field) stays empty, never "from the client"
  const viaRaw = f['Accepted Via']?.name || f['Accepted Via'] || acceptance?.via || '';
  const via = ACCEPTED_VIA[viaRaw] ? viaRaw : '';
  // The client's «No aceptar», from the existing history (Pipeline Facts Note)
  const declined = declineOf(f['Pipeline Facts Note']);

  return (
    <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'minmax(0, 1fr)' }}>
      {onBack && <div><button type="button" className="action-btn btn-ghost" style={{ fontSize: 12 }} onClick={onBack}>← Todas</button></div>}
      <div className="card" style={{ borderLeft: `3px solid ${kind.kind === 'contract' ? '#1E7B4F' : '#6B5CA5'}` }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: kind.kind === 'contract' ? '#1E7B4F' : '#6B5CA5' }}>{kind.label}</span>
          {summary ? <StagePill stage={summary.stage} /> : <span style={{ fontSize: 11 }}>{kind.state}</span>}
          {chain.kind === 'review' && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, color: '#9A6212', border: '1px solid #9A6212' }}>Completar datos del cliente</span>}
        </div>
        <h2 style={{ margin: '4px 0', fontSize: 20 }}>{title}</h2>
        <div style={muted}>{[subtitle, f['Name'] && f['Name'] !== title ? f['Name'] : '', recordTotalText(f)].filter(Boolean).join(' · ')}</div>
        {!recipient.explicit && <div style={{ ...muted, marginTop: 4 }}>Es anterior al destinatario explícito: se muestra según sus datos y no se cambia sola.</div>}
        {chain.kind === 'review' && <div role="note" style={{ fontSize: 12, color: '#9A6212', marginTop: 4 }}>No se puede saber con certeza a quién va: {classifyRecipient(f).reasons.join(' · ')}. Oike no lo cambia solo.</div>}
        {chain.anomalies.length > 0 && <div role="note" style={{ fontSize: 12, color: '#9A6212', marginTop: 4 }}>Sus versiones anteriores necesitan revisión en Airtable.</div>}
        {msg && <div role="status" style={{ fontSize: 12, marginTop: 8, fontWeight: 600 }}>{msg}</div>}
      </div>

      {/* The summary first; for a contratación, its installments and payments right below */}
      {summary && <FileSummary summary={summary} />}
      {top}
      {billing && chain.sale && billing(chain.sale.record, chain)}
      {!billing && chain.versions.some(v => v.record.fields?.['Payment Status'] || v.record.fields?.['Payment Received At']) && paymentNotesOf(chain).map((n, i) => <div key={i} style={{ fontSize: 12, color: n.tone === 'warn' ? '#9A6212' : 'var(--globant-muted)' }}>{n.text}</div>)}

      {/* 1. What is quoted: edited on this same record until the client accepts it */}
      <details open={kind.kind !== 'contract'}>
        <summary style={{ ...section, cursor: 'pointer' }}>{kind.kind === 'contract' ? 'Lo que se aceptó (servicios, importes y condiciones)' : 'Qué se cotiza'}</summary>
        <QuoteEditor quote={rec} account={recipient.value === 'company' || !recipient.explicit ? accountOf(rec) : null} contextName={title}
          manualOnly={recipient.explicit && recipient.value === 'person'} intakeFields={request?.fields || {}} intakeStatus={request ? 'Submitted' : ''}
          isFollowUp={!!f['Source Diagnostic']} saving={saving}
          onSave={(fields) => act(() => saveQuoteRecord(api, rec.id, fields), pending || landing ? 'Cambios guardados. El cliente los ve cuando los publiques.' : 'Cambios guardados.', 'No se pudo guardar')}
          onApprove={() => act(() => approveQuoteRecord(api, rec.id), 'Marcada como revisada (opcional).', 'No se pudo guardar')} />
      </details>

      {/* 2. Sending: the same document and link every time; each send keeps its date and total */}
      <div className="card">
        <div style={section}>Envío al cliente</div>
        {landing && !wasAccepted(f) && !declined && f['Quote Status'] !== 'Lost' && <ShareForResponse record={rec} viewLink={docLink(landing)} canShare={share !== false} />}
        {!wasAccepted(f) && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {(!landing || pending) && <button type="button" className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={!canSend || sending} onClick={send}>
              {sending ? 'Publicando…' : landing ? 'Publicar cambios' : 'Publicar para el cliente'}</button>}
            {landing && !pending && <span style={{ fontSize: 12, color: '#1E7B4F', fontWeight: 600 }}>El cliente tiene esta versión.</span>}
            {pending && <span role="note" style={{ fontSize: 12, color: '#9A6212' }}>Hay cambios guardados que el cliente todavía no ve.</span>}
            {!canSend && <span style={muted}>{SEND_BLOCK[sendProblem(f)] || 'Todavía no se puede publicar.'}</span>}
          </div>
        )}
        <div style={{ ...muted, marginTop: 6 }}>
          {wasAccepted(f) ? (landing ? 'Aceptada: el documento queda como se aceptó y no se vuelve a publicar.' : 'Aceptada sin documento publicado.') : 'Publicar actualiza siempre el mismo documento y enlace, y no envía correos. Para que el cliente responda, compartí su acceso personal con «Compartir para responder».'}
        </div>
        {docLink(landing) && <div style={{ fontSize: 12, marginTop: 4 }}><a href={docLink(landing)} target="_blank" rel="noreferrer">Ver el documento{wasAccepted(f) ? ' aceptado' : ''}</a></div>}
        {sends.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>Publicaciones</div>
            <ul style={{ margin: '4px 0 0 18px', fontSize: 12, lineHeight: 1.6 }}>
              {[...sends].reverse().map((s, i) => <li key={i}>{when(s.at)} · {totalsText(s, s.currency || f['Quote Currency'] || 'USD')}{s.legacy ? ' (publicación anterior a este historial)' : ''}</li>)}
            </ul>
          </div>
        )}
        {!sends.length && !landing && !wasAccepted(f) && <div style={{ ...muted, marginTop: 6 }}>Todavía no se publicó.</div>}
      </div>

      {/* 3. Acceptance: by the client from her space, or registered here; it freezes the accepted document */}
      <div className="card">
        <div style={section}>Aceptación</div>
        {declined && !isAccepted(f) ? (
          <div style={{ fontSize: 13 }}>
            <b style={{ color: '#B4362C' }}>No aceptada por el cliente</b>{declined.at ? ` el ${day(declined.at)}` : ''}{declined.by ? ` por ${declined.by}` : ''}{declined.via === 'client_space' ? ' · desde su espacio' : ''}.
            {declined.comment && <div style={{ marginTop: 4 }}>Comentario: «{declined.comment}»</div>}
            <div style={{ ...muted, marginTop: 4 }}>Queda en el historial de la cotización. El documento publicado no cambió.</div>
          </div>
        ) : isAccepted(f) || wasAccepted(f) ? (
          <div style={{ fontSize: 13 }}>
            {isAccepted(f) ? 'Aceptada' : kind.state}{f['Won At'] ? ` el ${day(f['Won At'])}` : ''}{f['Accepted By'] ? ` por ${f['Accepted By']}` : ''}{via ? ` · ${ACCEPTED_VIA[via] || via}` : ''}.
            <div style={{ ...muted, marginTop: 4 }}>{landing ? 'El documento aceptado queda tal cual (no se vuelve a publicar).' : 'Se aceptó sin documento publicado: el contenido de la ficha queda tal cual.'} Un cambio después de aceptar no se hace desde aquí.</div>
          </div>
        ) : accepting ? (
          <div style={{ display: 'grid', gap: 6, maxWidth: 420 }}>
            <div style={muted}>Registrá una aceptación que ya ocurrió fuera de Oike. Se acepta lo que el cliente tiene{landing ? ' (lo publicado)' : ' (el contenido actual: nunca se publicó)'}.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 6 }}>
              <label style={muted}>Día<input type="date" className="input-field" style={{ width: '100%' }} max={todayInput()} value={accepting.day} onChange={e => setAccepting(a => ({ ...a, day: e.target.value }))} /></label>
              <label style={muted}>Hora (opcional)<input type="time" className="input-field" style={{ width: '100%' }} value={accepting.time} onChange={e => setAccepting(a => ({ ...a, time: e.target.value }))} /></label>
            </div>
            <label style={muted}>Quién aceptó<input className="input-field" style={{ width: '100%' }} maxLength={120} value={accepting.by} onChange={e => setAccepting(a => ({ ...a, by: e.target.value }))} placeholder="Nombre o rol" /></label>
            <label style={{ fontSize: 12, display: 'flex', gap: 6 }}><input type="checkbox" checked={accepting.confirm} onChange={e => setAccepting(a => ({ ...a, confirm: e.target.checked }))} /> Confirmo que el cliente aceptó esta propuesta.</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={saving || !accepting.confirm || !accepting.day || accepting.by.trim().length < 2} onClick={accept}>{saving ? 'Guardando…' : 'Registrar aceptación'}</button>
              <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12 }} disabled={saving} onClick={() => setAccepting(null)}>Cancelar</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12 }} disabled={!!acceptBlock} onClick={() => setAccepting({ day: todayInput(), time: '', by: contactName || '', confirm: false })}>Registrar aceptación</button>
            <span style={muted}>{acceptBlock ? ACCEPT_BLOCK[acceptBlock] || '' : recipient.value === 'person' ? 'O la persona la acepta desde su espacio.' : 'Si el cliente aceptó por otro medio.'}</span>
          </div>
        )}
      </div>


      {/* Earlier versions (created before this file existed): read-only history, each with its own data */}
      {earlier.length > 0 && (
        <details>
          <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Versiones anteriores ({earlier.length})</summary>
          <div style={{ ...muted, margin: '6px 0' }}>Quedan como historial, cada una con su aceptación, pagos y documento. No se editan ni se crean nuevas.</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead><tr style={{ textAlign: 'left', color: 'var(--globant-muted)' }}><th>Versión</th><th>Estado</th><th>Importes</th><th>Aceptación</th><th>Documento</th><th></th></tr></thead>
              <tbody>
                {earlier.map(v => {
                  const vf = v.record.fields || {};
                  return (
                    <tr key={v.id} style={{ borderTop: '1px solid var(--globant-border)' }}>
                      <td>v{v.number}</td><td>{fileKindOf(vf).state === 'Historial' ? (isAccepted(vf) ? 'Aceptada' : wasAccepted(vf) ? 'Aceptación corregida' : 'Reemplazada') : fileKindOf(vf).state}</td>
                      <td>{recordTotalText(vf)}</td><td>{day(vf['Won At'])}{vf['Accepted By'] ? ` · ${vf['Accepted By']}` : ''}</td>
                      <td>{Array.isArray(vf['Consulting Landing']) && vf['Consulting Landing'].length ? 'Emitido' : '—'}</td>
                      <td><button type="button" className="action-btn btn-ghost" style={{ fontSize: 10 }} onClick={() => setViewEarlier(viewEarlier === v.id ? '' : v.id)}>{viewEarlier === v.id ? 'Cerrar' : 'Ver'}</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {viewEarlier && (() => {
            const v = earlier.find(x => x.id === viewEarlier);
            if (!v) return null;
            return (
              <div style={{ marginTop: 8 }}>
                <QuoteEditor key={v.id} quote={v.record} contextName={title} manualOnly saving onSave={() => {}} />
              </div>
            );
          })()}
        </details>
      )}
    </div>
  );
}
